import { all, call, put, takeEvery, select } from 'redux-saga/effects';
import AccountTypes from './AccountTypes';
import createRequest from '../../api/httpRequest';
import NotificationTypes from '../notification/NotificationTypes';
import StorageHelper from '../../utils/StorageHelper';
import { STORAGE_KEYS } from '../../configs/AppConfig';
import API_USER from '../../api/user';
import API_ACCOUNT_SETTINGS from '../../api/accountSetting';
import Util from '../../utils/Util';
import snakecaseKeys from 'snakecase-keys';
import { changeRoute } from '../route/RouteActions';
import { changeLocale } from '../locale/LocaleActions';
import { getAuthUser } from '../session/sessionSelector';
import SessionTypes from '../session/SessionTypes';
import CompanyTypes from '../company/CompanyTypes';
import { getCompanyDomain } from '../company/companySelector';

function * loadData () {
  yield takeEvery(AccountTypes.GET_ACCOUNT_SETTING_REQUEST, _loadData);
}

function * _loadData ({ payload }) {
  const user = yield select(getAuthUser);
  const api = { ...API_USER.GET_USER_DETAIL };
  api.url += user.id;
  yield createRequest({
    api: api,
    onSuccess: function * (response) {
      const data = Util.toCamelCaseKey(response.data);
      // invisible sidebar when mode is floated (maybe place in selectTodoSaga)
      // get list parent task
      yield put({
        type: AccountTypes.GET_ACCOUNT_SETTING_SUCCESS,
        payload: data
      });
    },
    onError: function * ({ status }) {
      yield put({
        type: AccountTypes.GET_ACCOUNT_SETTING_ERROR,
        payload: status
      });
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.error.${status}`,
            level: 'error',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
    }
  });
}

function * updateUserInfo () {
  yield takeEvery(AccountTypes.UPDATE_ACCOUNT_SETTING_REQUEST, _updateUserInfo);
}

function * _updateUserInfo ({ payload }) {
  const api = { ...API_USER.UPDATE_USER_INFO };
  api.url += payload.id;
  yield createRequest({
    api: Object.assign({}, api, {
      data: {
        first_name: payload.firstName,
        last_name: payload.lastName,
        lang: payload.lang
      }
    }),
    onSuccess: function * (data) {
      const userRes = data.data;
      // invisible sidebar when mode is floated (maybe place in selectTodoSaga)
      // get list parent task
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.update.${data.status}`,
            level: 'success',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });

      const user = yield select(getAuthUser);
      if (user.id === payload.id) {
        yield put({
          type: AccountTypes.GET_ACCOUNT_SETTING_REQUEST,
          payload: {}
        });
      }

      if (userRes.id === user.id) {
        yield call(StorageHelper.setLocalObject, STORAGE_KEYS.user, Util.toCamelCaseKey(userRes));
        yield put(changeLocale(userRes.lang));
      }
      if (payload.companyId) {
        yield put({
          type: CompanyTypes.GET_COMPANY_DETAIL_REQUEST,
          payload: payload.companyId
        });
      }
    },
    onError: function * ({ status }) {
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.error.${status}`,
            level: 'error',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
      yield put({
        type: AccountTypes.UPDATE_ACCOUNT_SETTING_ERROR,
        payload: status
      });
    }
  });
}

function * resetUserPassword () {
  yield takeEvery(AccountTypes.RESET_USER_PASSWORD_REQUEST, _resetUserPassword);
}

function * _resetUserPassword ({ payload }) {
  const api = { ...API_USER.RESET_USER_PASSWORD };
  api.url += payload;
  yield createRequest({
    api: api,
    onSuccess: function * () {
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: 'toast.success.passwordReset',
            level: 'success',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
    }
  });
}

function * changeUserPassword () {
  yield takeEvery(AccountTypes.CHANGE_PASSWORD_REQUEST, _changeUserPassword);
}

function * _changeUserPassword ({ payload }) {
  const request = snakecaseKeys(payload.request ? payload.request : payload);
  yield createRequest({
    api: {
      ...API_USER.CHANGE_USER_PASSWORD,
      data: request
    },
    onSuccess: function * (res) {
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: 'toast.success.passwordReset',
            level: 'success',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
      yield put({
        type: AccountTypes.SET_SHOW_MODAL,
        payload: false
      });
      if (payload.callback) {
        yield call(payload.callback, res);
      }
    },
    onError: function * (err) {
      if (payload.callback) {
        yield call(payload.callback, err);
      }
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.error.${err.status}`,
            level: 'error',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
    },
    onRequestError: function * (err) {
      if (payload.callback) {
        yield call(payload.callback, err);
      }
    }
  });
}

function * forgotPassword () {
  yield takeEvery(AccountTypes.FORGOT_PASSWORD_REQUEST, _forgotPassword);
}

function * _forgotPassword ({ payload }) {
  yield createRequest({
    api: {
      ...API_USER.FORGOT_PASSWORD,
      params: { ...payload }
    },
    onSuccess: function * () {
      yield put(changeRoute('/forget-password', `sent=true&email=${payload.email}`));
    }
  });
}

function * changeUserEmail () {
  yield takeEvery(AccountTypes.CHANGE_USER_EMAIL_REQUEST, _changeUserEmail);
}

function * _changeUserEmail ({ payload }) {
  const user = yield select(getAuthUser);
  const successMessage = 'api.update.200';
  // if (user.id === payload.id) {
  //   successMessage = 'toast.success.changeEmailRequest';
  // }
  const request = {
    email: payload.email
  };
  const api = { ...API_USER.CHANGE_USER_EMAIL };
  api.url += payload.id;
  yield createRequest({
    api: {
      ...api,
      data: request
    },
    onSuccess: function * (data) {
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: successMessage,
            level: 'success',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
      if (user.id === payload.id) {
        const newUserData = Util.toCamelCaseKey(data.data);
        yield call(StorageHelper.setLocalObject, STORAGE_KEYS.user, newUserData);
        yield put({ type: SessionTypes.RESTORE_SESSION_SUCCESS, payload: newUserData });
        yield put({ type: AccountTypes.GET_ACCOUNT_SETTING_REQUEST });
      } else {
        if (payload.companyId) {
          yield put({ type: CompanyTypes.GET_COMPANY_DETAIL_REQUEST, payload: payload.companyId });
        }
      }
    }
  });
}

function * fetchAccountSettingDetail () {
  yield takeEvery(AccountTypes.GET_ACCOUNT_SETTING_DETAIL, _fetchAccountSettingDetail);
}

function * _fetchAccountSettingDetail ({ payload }) {
  const api = {
    ...API_ACCOUNT_SETTINGS.GET_ACCOUNT_SETTING_DETAIL,
    params: {
      company_id: payload
    }
  };
  yield createRequest({
    api: api,
    onSuccess: function * (response) {
      const data = Util.toCamelCaseKey(response.data);
      // invisible sidebar when mode is floated (maybe place in selectTodoSaga)
      // get list parent task
      yield put({
        type: AccountTypes.GET_ACCOUNT_SETTING_DETAIL_SUCCESS,
        payload: data
      });
    }
  });
}

function * updateAccountSettingDetail () {
  yield takeEvery(AccountTypes.UPDATE_ACCOUNT_SETTING_DETAIL, _updateAccountSettingDetail);
}

function * _updateAccountSettingDetail ({ payload }) {
  const api = {
    ...API_ACCOUNT_SETTINGS.UPDATE_ACCOUNT_SETTING_DETAIL,
    data: snakecaseKeys(payload)
  };
  yield createRequest({
    api: api,
    onSuccess: function * (response) {
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: 'api.update.200',
            level: 'success',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
      yield put({
        type: AccountTypes.GET_ACCOUNT_SETTING_DETAIL,
        payload: payload.companyId
      });
    }
  });
}

function * fetchPasswordRegex () {
  yield takeEvery(AccountTypes.GET_PASSWORD_REGEX, _fetchPasswordRegex);
}

function * _fetchPasswordRegex ({ payload }) {
  const domain = yield select(getCompanyDomain);
  const api = {
    ...API_ACCOUNT_SETTINGS.GET_PASSWORD_REGEX,
    params: {
      domain: domain
    }
  };
  yield createRequest({
    api: api,
    onSuccess: function * (response) {
      const data = Util.toCamelCaseKey(response.data);
      // invisible sidebar when mode is floated (maybe place in selectTodoSaga)
      // get list parent task
      yield put({
        type: AccountTypes.GET_PASSWORD_REGEX_SUCCESS,
        payload: data
      });
    }
  });
}

function * resetAllAccountSettings () {
  yield takeEvery(AccountTypes.RESET_ALL_ACCOUNT_SETTINGS, _resetAllAccountSettings);
}

function * _resetAllAccountSettings ({ payload }) {
  const api = {
    ...API_ACCOUNT_SETTINGS.RESET_ALL_ACCOUNT_SETTINGS,
    params: {
      company_id: payload
    }
  };
  yield createRequest({
    api: api,
    onSuccess: function * (response) {
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: 'api.update.200',
            level: 'success',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
    }
  });
}

export default function * accountSaga () {
  yield all([
    loadData(),
    updateUserInfo(),
    resetUserPassword(),
    changeUserPassword(),
    forgotPassword(),
    changeUserEmail(),
    fetchAccountSettingDetail(),
    updateAccountSettingDetail(),
    fetchPasswordRegex(),
    resetAllAccountSettings()
  ]);
}
